/**
 * SealedPage (WO-20) - /sealed: every sealed product of a game (booster boxes, ETBs, bundles, packs, tins ...),
 * filterable by kind, sortable, paged. Entry point for sealed from the navbar, the series page and the home page.
 * URL: /sealed?type=<group key>&sort=newest|price_desc|price_asc|name&game=<game_id>
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageMeta from '../components/PageMeta';
import { ProductGrid } from '../components/ProductTile';
import GameTabs, { PillTabs } from '../components/GameTabs';
import { useCurrentGame, gameLabel } from '../lib/games';
import { groupsFromFacets, groupByKey } from '../lib/productTypes';

const PAGE = 40;
const SORTS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'price_desc', label: 'Price: High-Low' },
  { value: 'price_asc', label: 'Price: Low-High' },
  { value: 'name', label: 'Name: A-Z' },
];

export default function SealedPage() {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const [params, setParams] = useSearchParams();
  const { game, games, setGame } = useCurrentGame();
  const typeKey = params.get('type') || 'all';
  const sort = SORTS.some((s) => s.value === params.get('sort')) ? params.get('sort') : 'newest';
  const [facets, setFacets] = useState([]);
  const [total, setTotal] = useState(null);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('loading');
  const group = typeKey === 'all' ? null : groupByKey(typeKey);
  const typesParam = group ? group.types.join(',') : '';
  // "Load more" offset belongs to one filter combination; a new filter starts again at 0.
  const filterKey = `${game}|${typesParam}|${sort}`;
  const [paging, setPaging] = useState({ key: filterKey, offset: 0 });
  const offset = paging.key === filterKey ? paging.offset : 0;
  const setOffset = (n) => setPaging({ key: filterKey, offset: n });

  const setParam = (key, value, dflt) => {
    const p = new URLSearchParams(params);
    if (!value || value === dflt) p.delete(key); else p.set(key, value);
    setParams(p, { replace: true });
  };

  useEffect(() => {
    const ctrl = new AbortController();
    if (offset === 0) setStatus('loading');
    const q = new URLSearchParams({ game, sort, limit: String(PAGE), offset: String(offset), facets: 'true' });
    if (typesParam) q.set('product_type', typesParam);
    fetch(`${apiUrl}/api/products?${q}`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        const rows = Array.isArray(data.results) ? data.results : [];
        setItems((prev) => (offset === 0 ? rows : [...prev, ...rows]));
        setFacets(Array.isArray(data.types) ? data.types : []);
        setTotal(Number.isFinite(data.total) ? data.total : null);
        setStatus('ready');
      })
      .catch((e) => { if (e.name !== 'AbortError') setStatus('error'); });
    return () => ctrl.abort();
  }, [apiUrl, game, typesParam, sort, offset]);

  const groups = useMemo(() => groupsFromFacets(facets), [facets]);
  const allCount = groups.reduce((n, g) => n + g.count, 0);
  const label = gameLabel(game, games);
  const heading = group ? `${label} ${group.label}` : `${label} Sealed Products`;

  return (
    <>
      <PageMeta
        title={`${heading} | Prices and listings | Lilycove`}
        description={`${heading}: booster boxes, Elite Trainer Boxes, bundles, packs, tins and collections with TCGplayer market prices, store and eBay listings, and price history.`}
        url={`https://lilycove.io/sealed${typeKey !== 'all' ? `?type=${typeKey}` : ''}`}
      />
      <div className="flex items-center text-sm mb-6 text-cream/70">
        <Link to="/" className="text-skyblue hover:text-cream">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/series" className="text-skyblue hover:text-cream">{label}</Link>
        <span className="mx-2">/</span>
        <span>Sealed</span>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 gap-4">
        <div>
          <h1 className="text-2xl tracking-wider uppercase mb-1 font-medium text-purple" data-testid="sealed-heading">{heading}</h1>
          <p className="text-cream/70 text-sm tracking-wide">
            {total != null ? `${total.toLocaleString()} products` : ' '} · TCGplayer market prices, store and eBay listings
          </p>
        </div>
        <div className="flex gap-3 items-center w-full md:w-auto min-w-0">
          <GameTabs game={game} games={games} onChange={setGame} className="flex-1" />
          <select
            aria-label="Sort"
            value={sort}
            onChange={(e) => setParam('sort', e.target.value, 'newest')}
            className="h-9 rounded-md border border-purple/30 bg-dark px-3 py-1 text-cream text-sm"
          >
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <PillTabs
        label="Product type"
        className="mb-6"
        value={typeKey}
        onChange={(v) => setParam('type', v, 'all')}
        options={[{ value: 'all', label: 'All', count: allCount || null }, ...groups.map((g) => ({ value: g.key, label: g.label, count: g.count }))]}
      />

      {status === 'loading' && <p className="text-cream/70 py-10 text-center">Loading sealed products…</p>}
      {status === 'error' && <p className="text-cream/70 py-10 text-center">Sealed products are unavailable right now.</p>}
      {status === 'ready' && items.length === 0 && (
        <p className="text-cream/70 py-10 text-center">No sealed products here yet.</p>
      )}
      {status !== 'loading' && items.length > 0 && <ProductGrid items={items} />}
      {status === 'ready' && total != null && items.length < total && (
        <div className="flex justify-center mt-8">
          <button
            type="button"
            onClick={() => setOffset(items.length)}
            className="border border-purple/40 text-cream hover:bg-purple/20 tracking-wider uppercase text-sm py-2 px-6 rounded-md"
          >
            Load more ({(total - items.length).toLocaleString()} left)
          </button>
        </div>
      )}
    </>
  );
}
